/**
 * Telemetry API Client
 * Interfaces with orchestration-service telemetry endpoints
 */

import { useQuery } from '@tanstack/react-query';

const API_BASE = '/api/orchestration';

/* ── Types ─────────────────────────────────────────────────────── */

export interface TelemetryEvent {
  id: string;
  timestamp: string;
  eventType: string;
  eventCategory: string;
  severity: 'debug' | 'info' | 'warning' | 'error' | 'critical';
  workflowId: string | null;
  workflowType: string | null;
  runId: string | null;
  issueId: string | null;
  epicId: string | null;
  batchId: string | null;
  agentId: string | null;
  agentType: string | null;
  activityName: string | null;
  activityId: string | null;
  retryAttempt: number | null;
  payload: Record<string, unknown> | null;
  durationMs: number | null;
  memoryMb: number | null;
  cpuPercent: number | null;
  errorMessage: string | null;
  errorStack: string | null;
  errorCode: string | null;
  tags: string[] | null;
  source: string | null;
  traceId: string | null;
  parentId: string | null;
}

export interface TelemetryEventsResponse {
  events: TelemetryEvent[];
  count: number;
  filters: Record<string, unknown>;
}

export interface TelemetryEventFilters {
  limit?: number;
  offset?: number;
  eventType?: string;
  eventCategory?: string;
  severity?: string;
  workflowId?: string;
  issueId?: string;
  epicId?: string;
  agentId?: string;
  sprintId?: string;
  batchId?: string;
  startTime?: string;
  endTime?: string;
  /** Comma-separated event types to exclude server-side */
  excludeEventTypes?: string;
}

export interface HourlyMetric {
  bucket: string;
  workflow_type: string;
  event_category: string;
  event_count: string;
  error_count: string;
  critical_count: string;
  avg_duration_ms: string | null;
  max_duration_ms: string | null;
  min_duration_ms: string | null;
  median_duration_ms: string | null;
  p95_duration_ms: string | null;
  p99_duration_ms: string | null;
  workflow_count: string;
  agent_count: string;
  issue_count: string;
}

export interface TelemetryMetricsResponse {
  aggregate: string;
  metrics: HourlyMetric[];
  count: number;
}

export interface TelemetryDashboardData {
  timestamp: string;
  errors: {
    current: {
      total: number;
      critical: number;
      high: number;
      medium: number;
      low: number;
      byType: Record<string, number>;
      recentErrors: Array<{
        id: string;
        timestamp: string;
        errorMessage: string;
        errorCode: string | null;
        severity: string;
        workflowId: string | null;
      }>;
    };
  };
  workflows: {
    summary: Array<{
      workflowType: string;
      running: number;
      completed: number;
      failed: number;
    }>;
  };
  agents: {
    active: Array<{
      agentId: string;
      agentType: string;
      lastSeen: string;
    }>;
  };
  hourlyMetrics: HourlyMetric[];
}

export interface TelemetryErrorsResponse {
  errors: TelemetryEvent[];
  statistics: {
    totalErrors: number;
    resolvedErrors: number;
    openErrors: number;
    errorsByType: Record<string, number>;
    errorsBySeverity: Record<string, number>;
    errorTrend: Array<{ bucket: string; count: number }>;
  };
  filters: Record<string, unknown>;
}

export interface WorkflowSummary {
  workflowId: string;
  workflowType: string;
  status: string;
  eventCount: number;
  errorCount: number;
  firstEvent: string;
  lastEvent: string;
  duration: number | null;
}

/* ── Unified Timeline Types ───────────────────────────────────── */

export type UnifiedEventSource = 'temporal-history' | 'custom-telemetry' | 'sprite' | 'temporal' | 'system' | 'hook' | 'dashboard' | 'ml';

export type UnifiedCategory =
  | 'workflow' | 'activity' | 'timer' | 'signal'
  | 'child-workflow' | 'sprite' | 'build' | 'test' | 'deploy' | 'system';

export type UnifiedSeverity = 'info' | 'success' | 'warning' | 'error' | 'critical';

export interface UnifiedTimelineEvent {
  sequenceId: number;
  timestamp: string;
  temporalEventId?: number;
  customEventId?: number;
  source: UnifiedEventSource;
  eventType: string;
  category: UnifiedCategory;
  severity: UnifiedSeverity;
  title: string;
  description?: string;
  payload?: Record<string, unknown>;
  scheduledEventId?: number;
  activityType?: string;
  childWorkflowId?: string;
  failure?: { message: string; type?: string };
}

export interface ActivityLifecycle {
  activityType: string;
  activityId: string;
  scheduledEvent: { eventId: number; eventTime: string };
  startedEvent?: { eventId: number; eventTime: string };
  completedEvent?: { eventId: number; eventTime: string };
  failedEvent?: { eventId: number; eventTime: string };
  timedOutEvent?: { eventId: number; eventTime: string };
  scheduledToStartMs?: number;
  startedToCompletedMs?: number;
  totalDurationMs?: number;
  status: 'scheduled' | 'started' | 'completed' | 'failed' | 'timed-out';
  inputMeta?: { storyId?: string; epicId?: string; spriteId?: string };
}

export interface ChildWorkflowNode {
  workflowId: string;
  workflowType: string;
  status: string;
  activityCount: number;
  durationMs?: number;
  children: ChildWorkflowNode[];
}

export interface UnifiedTimelineResponse {
  events: UnifiedTimelineEvent[];
  activityLifecycles: ActivityLifecycle[];
  childWorkflows: Array<{
    workflowId: string;
    workflowType: string;
    parentWorkflowId: string;
    status: string;
  }>;
  totalCount: number;
  temporalEventCount: number;
  customEventCount: number;
  workflowStatus: string;
  temporalAvailable: boolean;
}

export interface TemporalHistoryResponse {
  workflowId: string;
  events: Array<{
    eventId: number;
    eventTime: string;
    eventType: string;
    category: string;
    attributes: Record<string, unknown>;
    activityType?: string;
    signalName?: string;
    childWorkflowId?: string;
    failure?: { message: string; type?: string; stackTrace?: string };
    scheduledEventId?: number;
  }>;
  activityLifecycles: ActivityLifecycle[];
  childWorkflows: Array<{
    workflowId: string;
    workflowType: string;
    parentWorkflowId: string;
    status: string;
  }>;
  totalEvents: number;
  workflowStatus: string;
}

export interface UnifiedTimelineFilters {
  source?: UnifiedEventSource;
  category?: UnifiedCategory;
  severity?: UnifiedSeverity;
  limit?: number;
  offset?: number;
}

/* ── API Client ────────────────────────────────────────────────── */

export const telemetryApi = {
  async getEvents(filters: TelemetryEventFilters = {}): Promise<TelemetryEventsResponse> {
    const params = new URLSearchParams();
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.offset) params.set('offset', String(filters.offset));
    if (filters.eventType) params.set('eventType', filters.eventType);
    if (filters.eventCategory) params.set('eventCategory', filters.eventCategory);
    if (filters.severity) params.set('severity', filters.severity);
    if (filters.workflowId) params.set('workflowId', filters.workflowId);
    if (filters.issueId) params.set('issueId', filters.issueId);
    if (filters.epicId) params.set('epicId', filters.epicId);
    if (filters.agentId) params.set('agentId', filters.agentId);
    if (filters.sprintId) params.set('sprintId', filters.sprintId);
    if (filters.batchId) params.set('batchId', filters.batchId);
    if (filters.startTime) params.set('startTime', filters.startTime);
    if (filters.endTime) params.set('endTime', filters.endTime);
    if (filters.excludeEventTypes) params.set('excludeEventTypes', filters.excludeEventTypes);

    const url = `${API_BASE}/telemetry/events${params.toString() ? `?${params}` : ''}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch telemetry events: ${response.statusText}`);
    return response.json();
  },

  async getWorkflowSummary(workflowId: string): Promise<WorkflowSummary> {
    const response = await fetch(`${API_BASE}/telemetry/workflows/${workflowId}/summary`);
    if (!response.ok) throw new Error(`Failed to fetch workflow summary: ${response.statusText}`);
    return response.json();
  },

  async getErrors(limit = 20): Promise<TelemetryErrorsResponse> {
    const response = await fetch(`${API_BASE}/telemetry/errors?limit=${limit}`);
    if (!response.ok) throw new Error(`Failed to fetch telemetry errors: ${response.statusText}`);
    return response.json();
  },

  async getMetrics(aggregate: string, opts?: { limit?: number; startTime?: string; endTime?: string }): Promise<TelemetryMetricsResponse> {
    const params = new URLSearchParams();
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.startTime) params.set('startTime', opts.startTime);
    if (opts?.endTime) params.set('endTime', opts.endTime);

    const url = `${API_BASE}/telemetry/metrics/${aggregate}${params.toString() ? `?${params}` : ''}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch telemetry metrics: ${response.statusText}`);
    return response.json();
  },

  async getDashboard(): Promise<TelemetryDashboardData> {
    const response = await fetch(`${API_BASE}/telemetry/dashboard`);
    if (!response.ok) throw new Error(`Failed to fetch telemetry dashboard: ${response.statusText}`);
    return response.json();
  },

  async getAgentMetrics(agentId: string, hours = 24): Promise<{ metrics: HourlyMetric[] }> {
    const response = await fetch(`${API_BASE}/telemetry/agents/${agentId}/metrics?hours=${hours}`);
    if (!response.ok) throw new Error(`Failed to fetch agent metrics: ${response.statusText}`);
    return response.json();
  },

  async getTemporalHistory(workflowId: string, opts?: { category?: string; includeWorkflowTasks?: boolean }): Promise<TemporalHistoryResponse> {
    const params = new URLSearchParams();
    if (opts?.category) params.set('category', opts.category);
    if (opts?.includeWorkflowTasks) params.set('includeWorkflowTasks', 'true');
    const url = `${API_BASE}/telemetry/workflows/${workflowId}/temporal-history${params.toString() ? `?${params}` : ''}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch Temporal history: ${response.statusText}`);
    return response.json();
  },

  async getUnifiedTimeline(workflowId: string, opts?: UnifiedTimelineFilters): Promise<UnifiedTimelineResponse> {
    const params = new URLSearchParams();
    if (opts?.source) params.set('source', opts.source);
    if (opts?.category) params.set('category', opts.category);
    if (opts?.severity) params.set('severity', opts.severity);
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.offset) params.set('offset', String(opts.offset));
    const url = `${API_BASE}/telemetry/workflows/${workflowId}/unified-timeline${params.toString() ? `?${params}` : ''}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch unified timeline: ${response.statusText}`);
    return response.json();
  },

  async getChildWorkflowTree(workflowId: string): Promise<ChildWorkflowNode> {
    const response = await fetch(`${API_BASE}/telemetry/workflows/${workflowId}/child-workflows`);
    if (!response.ok) throw new Error(`Failed to fetch child workflows: ${response.statusText}`);
    return response.json();
  },
};

/* ── React Query Hooks ─────────────────────────────────────────── */

export function useTelemetryEvents(filters: TelemetryEventFilters = {}) {
  return useQuery({
    queryKey: ['telemetry', 'events', filters],
    queryFn: () => telemetryApi.getEvents(filters),
    refetchInterval: 5000,
    staleTime: 3000,
  });
}

export function useTelemetryDashboard() {
  return useQuery({
    queryKey: ['telemetry', 'dashboard'],
    queryFn: () => telemetryApi.getDashboard(),
    refetchInterval: 15000,
    staleTime: 10000,
  });
}

export function useTelemetryMetrics(aggregate: string, opts?: { limit?: number; startTime?: string; endTime?: string }) {
  return useQuery({
    queryKey: ['telemetry', 'metrics', aggregate, opts],
    queryFn: () => telemetryApi.getMetrics(aggregate, opts),
    refetchInterval: 30000,
    staleTime: 20000,
  });
}

export function useWorkflowSummary(workflowId: string | undefined) {
  return useQuery({
    queryKey: ['telemetry', 'workflow-summary', workflowId],
    queryFn: () => telemetryApi.getWorkflowSummary(workflowId!),
    enabled: !!workflowId,
    refetchInterval: 10000,
    staleTime: 5000,
  });
}

export function useTelemetryErrors(limit = 20) {
  return useQuery({
    queryKey: ['telemetry', 'errors', limit],
    queryFn: () => telemetryApi.getErrors(limit),
    refetchInterval: 15000,
    staleTime: 10000,
  });
}

export function useUnifiedTimeline(workflowId: string | undefined, opts?: UnifiedTimelineFilters) {
  return useQuery({
    queryKey: ['telemetry', 'unified-timeline', workflowId, opts],
    queryFn: () => telemetryApi.getUnifiedTimeline(workflowId!, opts),
    enabled: !!workflowId,
    refetchInterval: 5000,
    staleTime: 3000,
  });
}

export function useChildWorkflowTree(workflowId: string | undefined) {
  return useQuery({
    queryKey: ['telemetry', 'child-workflow-tree', workflowId],
    queryFn: () => telemetryApi.getChildWorkflowTree(workflowId!),
    enabled: !!workflowId,
    refetchInterval: 10000,
    staleTime: 5000,
  });
}

/* ── Error Summary Types ──────────────────────────────────────── */

export interface ErrorSummaryEntry {
  errorText: string;
  errorCode: string | null;
  severity: string;
  count: number;
  sources: string[];
}

export interface ErrorSummaryResponse {
  workflowId: string;
  totalErrors: number;
  topError: ErrorSummaryEntry | null;
  errors: ErrorSummaryEntry[];
}

/* ── Error Summary API + Hook ────────────────────────────────── */

export async function getErrorSummary(workflowId: string): Promise<ErrorSummaryResponse> {
  const response = await fetch(`${API_BASE}/telemetry/workflows/${workflowId}/error-summary`);
  if (!response.ok) throw new Error(`Failed to fetch error summary: ${response.statusText}`);
  return response.json();
}

export function useSprintErrorSummary(workflowId: string | undefined) {
  return useQuery({
    queryKey: ['telemetry', 'error-summary', workflowId],
    queryFn: () => getErrorSummary(workflowId!),
    enabled: !!workflowId,
    refetchInterval: 10000,
    staleTime: 5000,
  });
}

/* ── Sprite Activity Types ────────────────────────────────────── */

export interface SpriteActivityEvent {
  id: string;
  timestamp: string;
  event_type: string;
  issue_id: string | null;
  epic_id: string | null;
  agent_id: string | null;
  severity: string;
  phase: string | null;
  file_path: string | null;
  message: string | null;
  lines_added: string | null;
  test_count: string | null;
  commit_sha: string | null;
  error_count: string | null;
  attempt_number: string | null;
  prior_error: string | null;
  story_id: string | null;
  status: string | null;
}

export interface SpriteActivityResponse {
  events: SpriteActivityEvent[];
  count: number;
  batchId: string;
}

/* ── Sprite Activity API + Hook ──────────────────────────────── */

export async function getSpriteActivity(
  batchId: string,
  opts?: { limit?: number; issueId?: string; agentId?: string }
): Promise<SpriteActivityResponse> {
  const params = new URLSearchParams({ batchId });
  if (opts?.limit) params.set('limit', String(opts.limit));
  if (opts?.issueId) params.set('issueId', opts.issueId);
  if (opts?.agentId) params.set('agentId', opts.agentId);
  const response = await fetch(`${API_BASE}/telemetry/sprite-activity?${params}`);
  if (!response.ok) throw new Error(`Failed to fetch sprite activity: ${response.statusText}`);
  return response.json();
}

export function useSpriteActivity(
  batchId: string | undefined,
  opts?: { enabled?: boolean; limit?: number; issueId?: string; agentId?: string }
) {
  return useQuery({
    queryKey: ['telemetry', 'sprite-activity', batchId, opts?.issueId, opts?.agentId],
    queryFn: () => getSpriteActivity(batchId!, { limit: opts?.limit, issueId: opts?.issueId, agentId: opts?.agentId }),
    enabled: !!batchId && (opts?.enabled !== false),
    refetchInterval: 5000,
    staleTime: 3000,
  });
}

/* ── File Activity Types ──────────────────────────────────────── */

export interface FileEvent {
  id: string;
  timestamp: string;
  operation: 'created' | 'modified' | 'deleted';
  filePath: string;
  issueId: string | null;
  storyId: string | null;
  language: string | null;
  linesAdded: number | null;
}

export interface FileActivityResponse {
  files: FileEvent[];
  count: number;
  batchId: string;
}

/* ── File Activity API + Hook ────────────────────────────────── */

export async function getFileActivity(batchId: string): Promise<FileActivityResponse> {
  const params = new URLSearchParams({ batchId, limit: '500' });
  const response = await fetch(`${API_BASE}/telemetry/sprite-activity?${params}`);
  if (!response.ok) throw new Error(`Failed to fetch file activity: ${response.statusText}`);
  const data: SpriteActivityResponse = await response.json();

  // Filter to file events and normalize
  const fileEvents = data.events
    .filter(e => e.event_type.startsWith('sprite_file_'))
    .map(e => ({
      id: e.id,
      timestamp: e.timestamp,
      operation: e.event_type.replace('sprite_file_', '') as FileEvent['operation'],
      filePath: e.file_path || 'unknown',
      issueId: e.issue_id,
      storyId: e.story_id || e.issue_id,
      language: guessLanguage(e.file_path),
      linesAdded: e.lines_added ? parseInt(e.lines_added, 10) : null,
    }));

  return { files: fileEvents, count: fileEvents.length, batchId };
}

function guessLanguage(filePath: string | null): string | null {
  if (!filePath) return null;
  const ext = filePath.split('.').pop()?.toLowerCase();
  const map: Record<string, string> = {
    ts: 'TypeScript', tsx: 'TypeScript', js: 'JavaScript', jsx: 'JavaScript',
    py: 'Python', rs: 'Rust', go: 'Go', java: 'Java', rb: 'Ruby',
    css: 'CSS', scss: 'SCSS', html: 'HTML', json: 'JSON', yaml: 'YAML',
    yml: 'YAML', md: 'Markdown', sql: 'SQL', sh: 'Shell', dockerfile: 'Docker',
  };
  return ext ? map[ext] || ext.toUpperCase() : null;
}

export function useFileActivity(batchId: string | undefined) {
  return useQuery({
    queryKey: ['telemetry', 'file-activity', batchId],
    queryFn: () => getFileActivity(batchId!),
    enabled: !!batchId,
    refetchInterval: 8000,
    staleTime: 5000,
  });
}

export default telemetryApi;
